import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { exportAssessments } from "../helpers/assessmentExport";

import Api from "../../../shared/api/api";
import { getStoredOrLatestTestId } from "../helpers/fourHTest";

import CoordinatorList from "./CoordinatorList";
import ReadyToCreateList from "./ReadyToCreateList";
import WarningMsg from "./WarningMsg";
import CreatePDFModal from "./CreatePDFModal";
import CreateEmail from "./CreateEmail";

import "../styles/CoordinatorListItems.css";

const getAssessmentKey = (assessment) =>
  `${assessment.user_id}-${assessment.test_id}`;

const parseAssessmentAnswers = (assessment) => {
  if (Array.isArray(assessment?.answers)) {
    return assessment.answers;
  }

  if (typeof assessment?.answers !== "string") {
    return [];
  }

  try {
    const parsedAnswers = JSON.parse(assessment.answers);
    return Array.isArray(parsedAnswers)
      ? parsedAnswers
      : [];
  } catch (error) {
    console.error(
      "Unable to parse assessment answers:",
      assessment.answers,
      error
    );

    return [];
  }
};

const calculateTotalScore = (assessment) => {
  const knowledgeScore =
    Number.parseInt(assessment.knowledge_score, 10) || 0;

  const identifyScore =
    Number.parseInt(assessment.identify_score, 10) || 0;

  return knowledgeScore * 3 + identifyScore * 3;
};

export default function CoordinatorListItems() {
  const { id: coordinatorId } = useParams();

  const [assessments, setAssessments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [testId, setTestId] = useState(null);

  const [identityScores, setIdentityScores] = useState({});
  const [givenPoints, setGivenPoints] = useState({});
  const [temporaryCorrections, setTemporaryCorrections] =
    useState({});

  const [activeAssessmentKey, setActiveAssessmentKey] =
    useState("");

  const activeAssessmentRef = useRef(null);

  const [createPDFModalIsActive, setCreatePDFModalIsActive] =
    useState(false);

  const [selectedAssessmentKey, setSelectedAssessmentKey] =
    useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadCoordinatorData = async () => {
      setIsLoading(true);
      setError("");

      try {
        const latestTestId =
          await getStoredOrLatestTestId();

        setTestId(latestTestId);

        const [
          assessmentResponse,
          userResponse,
          questionResponse,
        ] = await Promise.all([
          Api.getAll("assessment"),
          Api.getAll("users"),
          latestTestId
            ? Api.getTestQuestions(latestTestId)
            : Promise.resolve([]),
        ]);

        if (assessmentResponse?.error) {
          throw new Error(assessmentResponse.error);
        }

        if (userResponse?.error) {
          throw new Error(userResponse.error);
        }

        if (questionResponse?.error) {
          throw new Error(questionResponse.error);
        }

        setAssessments(
          Array.isArray(assessmentResponse)
            ? assessmentResponse
            : []
        );

        setUsers(
          Array.isArray(userResponse)
            ? userResponse
            : []
        );

        setQuestions(
          Array.isArray(questionResponse)
            ? questionResponse
            : []
        );
      } catch (requestError) {
        console.error(
          "Unable to load coordinator data:",
          requestError
        );

        setError(
          requestError?.message ||
            "Coordinator data could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadCoordinatorData();
  }, []);

  const tests = useMemo(() => {
    const uniqueTests = new Map();

    assessments.forEach((assessment) => {
      parseAssessmentAnswers(assessment).forEach((answer) => {
        const key = JSON.stringify(answer);

        if (!uniqueTests.has(key)) {
          uniqueTests.set(key, answer);
        }
      });
    });

    return Array.from(uniqueTests.values());
  }, [assessments]);

  const activeAssessment = useMemo(
    () =>
      assessments.find(
        (assessment) =>
          getAssessmentKey(assessment) ===
          activeAssessmentKey
      ) || null,
    [assessments, activeAssessmentKey]
  );

  useEffect(() => {
    activeAssessmentRef.current = activeAssessment;
  }, [activeAssessment]);

  useEffect(() => {
    return () => {
      const assessment = activeAssessmentRef.current;

      if (assessment) {
        Api.unlockAssessment(
          assessment.user_id,
          assessment.test_id
        ).catch((unlockError) => {
          console.error(
            "Unable to release assessment lock:",
            unlockError
          );
        });
      }
    };
  }, []);

  const handleGivePoints = (userId, questionId) => {
    const correctionKey = `${userId}-${questionId}`;

    if (temporaryCorrections[correctionKey]) {
      return;
    }

    setIdentityScores((currentScores) => ({
      ...currentScores,
      [userId]: (currentScores[userId] || 0) + 1,
    }));

    setGivenPoints((currentPoints) => ({
      ...currentPoints,
      [correctionKey]: true,
    }));

    setTemporaryCorrections((currentCorrections) => ({
      ...currentCorrections,
      [correctionKey]: true,
    }));
  };

  const applyCorrectionsToAssessment = (
    assessment,
    userId
  ) => {
    const updatedAnswers = parseAssessmentAnswers(
      assessment
    ).map((answer) => {
      const correctionKey = `${userId}-${answer.questionId}`;

      if (!temporaryCorrections[correctionKey]) {
        return answer;
      }

      return {
        ...answer,
        corrected: true,
      };
    });

    return {
      ...assessment,
      answers: updatedAnswers,
    };
  };

  const handleToggleAssessment = async (assessment) => {
    const assessmentKey = getAssessmentKey(assessment);

    setError("");

    try {
      if (activeAssessmentKey === assessmentKey) {
        await Api.unlockAssessment(
          assessment.user_id,
          assessment.test_id
        );

        setActiveAssessmentKey("");
        return;
      }

      if (activeAssessment) {
        await Api.unlockAssessment(
          activeAssessment.user_id,
          activeAssessment.test_id
        );
      }

      const lockStatus = await Api.isLocked(
        assessment.user_id,
        assessment.test_id
      );

      if (lockStatus?.error) {
        throw new Error(lockStatus.error);
      }

      if (lockStatus?.locked) {
        throw new Error(
          "This assessment is currently being reviewed."
        );
      }

      const lockResponse = await Api.lockAssessment(
        coordinatorId,
        assessment.user_id,
        assessment.test_id
      );

      if (lockResponse?.error) {
        throw new Error(lockResponse.error);
      }

      setActiveAssessmentKey(assessmentKey);
    } catch (requestError) {
      console.error(
        "Unable to open assessment:",
        requestError
      );

      setError(
        requestError?.message ||
          "The assessment could not be opened."
      );
    }
  };

  const handleReview = async (assessment) => {
    const userId = assessment.user_id;
    const assessmentTestId = assessment.test_id;

    const updatedAssessment =
      applyCorrectionsToAssessment(
        assessment,
        userId
      );

    const currentIdentifyScore =
      Number(assessment.identify_score) || 0;

    const identifyScore =
      currentIdentifyScore +
      (identityScores[userId] || 0);

    setIsSaving(true);
    setError("");

    try {
      const response = await Api.updateUserAssessment(
        userId,
        assessmentTestId,
        {
          identifyScore,
          readyToSendToLeader: true,
          readyForCoordinator: false,
          locked: false,
          locked_by: null,
          answers: JSON.stringify(
            updatedAssessment.answers
          ),
        }
      );

      if (response?.error) {
        throw new Error(response.error);
      }

      await Api.unlockAssessment(
        userId,
        assessmentTestId
      );

      setAssessments((currentAssessments) =>
        currentAssessments.map((currentAssessment) =>
          getAssessmentKey(currentAssessment) ===
          getAssessmentKey(assessment)
            ? {
                ...updatedAssessment,
                identify_score: identifyScore,
                ready_for_coordinator: 0,
                ready_to_send_to_leader: 1,
                locked: 0,
                locked_by: null,
              }
            : currentAssessment
        )
      );

      setActiveAssessmentKey("");
      setIdentityScores((currentScores) => {
        const nextScores = { ...currentScores };
        delete nextScores[userId];
        return nextScores;
      });

      setTemporaryCorrections({});
      setGivenPoints({});
    } catch (requestError) {
      console.error(
        "Unable to save assessment review:",
        requestError
      );

      setError(
        requestError?.message ||
          "The assessment review could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreatePDF = (userId, assessmentTestId) => {
    setSelectedAssessmentKey(
      `${userId}-${assessmentTestId}`
    );

    setCreatePDFModalIsActive(true);
  };

  const handlePDFSubmit = async (
    userId,
    assessmentTestId,
    base64Pages,
    team
  ) => {
    setError("");

    try {
      const response = await Api.addUserPdf({
        userId,
        testId: assessmentTestId,
        page_one: base64Pages.page_one,
        page_two: base64Pages.page_two,
        team,
      });

      if (response?.error) {
        throw new Error(response.error);
      }

      const updateResponse =
        await Api.updateUserAssessment(
          userId,
          assessmentTestId,
          {
            readyToSendToLeader: false,
          }
        );

      if (updateResponse?.error) {
        throw new Error(updateResponse.error);
      }

      setAssessments((currentAssessments) =>
        currentAssessments.map((assessment) =>
          assessment.user_id === userId &&
          assessment.test_id === assessmentTestId
            ? {
                ...assessment,
                ready_to_send_to_leader: 0,
              }
            : assessment
        )
      );

      setCreatePDFModalIsActive(false);
      setSelectedAssessmentKey("");
    } catch (requestError) {
      console.error(
        "Unable to save assessment PDF:",
        requestError
      );

      setError(
        requestError?.message ||
          "The PDF could not be saved."
      );
    }
  };

  const exportToExcel = async () => {
    const data = assessments
      .filter((assessment) => assessment.sent)
      .map((assessment) => {
        const user = users.find(
          (candidate) =>
            candidate.four_h_id ===
            assessment.user_id
        );

        if (!user) {
          return null;
        }

        return {
          Name: [
            user.name_first,
            user.name_middle,
            user.name_last,
          ]
            .filter(Boolean)
            .join(" "),
          ID: assessment.user_id,
          Level: user.level,
          Score: calculateTotalScore(assessment),
        };
      })
      .filter(Boolean);

    if (data.length === 0) {
      setError(
        "There are no sent assessments to export."
      );
      return;
    }

    try { await exportAssessments(data, testId); }
    catch { setError("Unable to export assessments. Please try again."); }
  };

  if (isLoading) {
    return (
      <div className="coordinatorConsole">
        <p role="status">Loading assessments...</p>
      </div>
    );
  }

  const readyForCoordinator = assessments.filter(
    (assessment) =>
      Boolean(assessment.ready_for_coordinator)
  );

  const readyForPDF = assessments.filter(
    (assessment) =>
      Boolean(assessment.ready_to_send_to_leader)
  );

  const readyForEmail = assessments.filter(
    (assessment) =>
      !assessment.ready_for_coordinator &&
      !assessment.ready_to_send_to_leader &&
      !assessment.sent
  );

  const sentAssessments = assessments.filter(
    (assessment) => Boolean(assessment.sent)
  );

  return (
    <div className="coordinatorConsole">
      {error && (
        <WarningMsg message={error} />
      )}

      {assessments.length === 0 ? (
        <WarningMsg message="There are currently no assessments for review." />
      ) : (
        <>
          {readyForCoordinator.length > 0 && (
            <section>
              <h1 className="assessmentStatusTitle">
                Ready for Coordinator
              </h1>

              {readyForCoordinator.map((assessment) => {
                const assessmentKey =
                  getAssessmentKey(assessment);

                if (
                  activeAssessmentKey === assessmentKey
                ) {
                  return (
                    <CoordinatorList
                      key={assessmentKey}
                      assessment={assessment}
                      tests={tests}
                      givenPoints={givenPoints}
                      handleGivePoints={handleGivePoints}
                      handleReview={handleReview}
                      isSubmitting={isSaving}
                    />
                  );
                }

                return (
                  <button
                    key={assessmentKey}
                    type="button"
                    className={`assessmentListItem${
                      assessment.locked
                        ? " locked"
                        : ""
                    }`}
                    onClick={() =>
                      handleToggleAssessment(assessment)
                    }
                    disabled={Boolean(
                      assessment.locked
                    )}
                  >
                    {assessment.user_id} –{" "}
                    {assessment.test_id} Assessment
                  </button>
                );
              })}
            </section>
          )}

          {readyForPDF.length > 0 && (
            <section>
              <h1 className="assessmentStatusTitle">
                Ready to Create PDF
              </h1>

              {readyForPDF.map((assessment) => {
                const assessmentKey =
                  getAssessmentKey(assessment);

                if (
                  createPDFModalIsActive &&
                  selectedAssessmentKey ===
                    assessmentKey
                ) {
                  return (
                    <CreatePDFModal
                      key={assessmentKey}
                      userId={assessment.user_id}
                      testId={assessment.test_id}
                      users={users}
                      assessments={assessments}
                      questions={questions}
                      handlePDFSubmit={handlePDFSubmit}
                      onCancel={() => {
                        setCreatePDFModalIsActive(false);
                        setSelectedAssessmentKey("");
                      }}
                    />
                  );
                }

                return (
                  <ReadyToCreateList
                    key={assessmentKey}
                    a={assessment}
                    handleCreatePDF={() =>
                      handleCreatePDF(
                        assessment.user_id,
                        assessment.test_id
                      )
                    }
                  />
                );
              })}
            </section>
          )}

          {readyForEmail.length > 0 && (
            <section>
              <h1 className="assessmentStatusTitle">
                Ready to Send to Leader
              </h1>

              {readyForEmail.map((assessment) => (
                <CreateEmail
                  key={getAssessmentKey(assessment)}
                  userId={assessment.user_id}
                  testId={assessment.test_id}
                  users={users}
                  onSent={() =>
                    setAssessments(
                      (currentAssessments) =>
                        currentAssessments.map(
                          (currentAssessment) =>
                            getAssessmentKey(
                              currentAssessment
                            ) ===
                            getAssessmentKey(assessment)
                              ? {
                                  ...currentAssessment,
                                  sent: 1,
                                }
                              : currentAssessment
                        )
                    )
                  }
                />
              ))}
            </section>
          )}

          {sentAssessments.length > 0 && (
            <section className="sentAssessmentsSection">
              <h1 className="sentAssessmentsHeader">
                Assessments Sent to Leader and Participant
              </h1>

              <button
                className="sentAssessmentsBtn"
                type="button"
                onClick={exportToExcel}
              >
                Export to Excel
              </button>

              <div className="fourHTableWrapper">
                <table className="listOfSentAssessments">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">ID</th>
                      <th scope="col">Level</th>
                      <th scope="col">Score</th>
                    </tr>
                  </thead>

                  <tbody>
                    {sentAssessments.map(
                      (assessment) => {
                        const user = users.find(
                          (candidate) =>
                            candidate.four_h_id ===
                            assessment.user_id
                        );

                        if (!user) {
                          return null;
                        }

                        return (
                          <tr
                            key={getAssessmentKey(
                              assessment
                            )}
                          >
                            <td>
                              {[
                                user.name_first,
                                user.name_middle,
                                user.name_last,
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            </td>

                            <td>
                              {assessment.user_id}
                            </td>

                            <td>{user.level}</td>

                            <td>
                              <strong>
                                {calculateTotalScore(
                                  assessment
                                )}
                              </strong>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
